import type { ReactNode } from 'react';
import { Link } from '@tanstack/react-router';
import type { TaskResponse } from '@erp/rpc';
import {
  useTable,
  appTableFeatures,
  type SortingState,
  type Updater,
} from '@/lib/tanstack-table';
import { DataTable } from 'src/components/common/data-table/data-table';
import { DataTablePagination } from 'src/components/common/data-table/data-table-pagination';
import type { TaskQueueController } from './useTasksQueuePage';
import type { TaskQueueTableMeta } from './taskQueueCells';
import type { TasksQueueSearch } from './taskQueueSearch';
import { TaskQueueBulkActionsBar } from './TaskQueueBulkActionsBar';
import { SnoozeQueueNotice } from './SnoozeQueueNotice';
import { SnoozeTaskDialog } from './SnoozeTaskDialog';
import { DeleteTaskDialog } from './$taskId/TaskDeleteControls';
import { isActivelySnoozed, snoozeQueueNotice } from './taskSnooze';
import { useTaskRowContextMenu } from './useTaskRowContextMenu';

function nextSortingSearch({
  updater,
  sortingState,
  toSortField,
}: {
  updater: Updater<SortingState>;
  sortingState: SortingState;
  toSortField: (
    id: string | undefined,
  ) => TasksQueueSearch['sortBy'] | undefined;
}): Pick<TasksQueueSearch, 'sortBy' | 'sortOrder'> {
  const next = typeof updater === 'function' ? updater(sortingState) : updater;
  const sort = next[0];
  return {
    sortBy: toSortField(sort?.id),
    sortOrder: sort ? (sort.desc ? 'desc' : 'asc') : undefined,
  };
}

function nextPaginationSearch({
  updater,
  pageIndex,
  pageSize,
}: {
  updater: Updater<{ pageIndex: number; pageSize: number }>;
  pageIndex: number;
  pageSize: number;
}): Pick<TasksQueueSearch, 'page' | 'pageSize'> {
  const next =
    typeof updater === 'function' ? updater({ pageIndex, pageSize }) : updater;
  return {
    page: next.pageIndex + 1,
    ...(next.pageSize !== pageSize ? { pageSize: next.pageSize } : {}),
  };
}

/**
 * The tasks-queue table + pagination over a {@link TaskQueueController},
 * shared by `/tasks` and the `/pages/$taskPageId` task pages. Rows always link
 * to `/tasks/$taskId` (optionally with `?fromPage=` so breadcrumbs can return
 * to a task page).
 */
export function TaskQueueView({
  queue,
  toolbar,
}: {
  queue: TaskQueueController;
  toolbar: ReactNode;
}) {
  const detailSearch = queue.detailSearch;
  const snoozeNotice = snoozeNoticeMode(queue);
  const rowMenu = useTaskRowContextMenu(
    (task: TaskResponse) => queue.openSnooze([task.id]),
    (task: TaskResponse) => queue.openDelete(task.id),
  );

  const tableMeta: TaskQueueTableMeta = { taskQueueLookup: queue.cellLookup };
  const table = useTable({
    features: appTableFeatures,
    columns: queue.columns,
    data: queue.tasks,
    manualPagination: true,
    manualSorting: true,
    enableRowSelection: queue.bulk.enabled,
    rowCount: queue.total,
    state: {
      pagination: { pageIndex: queue.pageIndex, pageSize: queue.pageSize },
      sorting: queue.sortingState,
      rowSelection: queue.bulk.rowSelection,
    },
    getRowId: (row) => row.id,
    meta: tableMeta,
    onRowSelectionChange: queue.bulk.setRowSelection,
    onSortingChange: (updater) => {
      queue.updateSearchParams(
        nextSortingSearch({
          updater,
          sortingState: queue.sortingState,
          toSortField: queue.toSortField,
        }),
        true,
      );
    },
    onPaginationChange: (updater) => {
      queue.updateSearchParams(
        nextPaginationSearch({
          updater,
          pageIndex: queue.pageIndex,
          pageSize: queue.pageSize,
        }),
      );
    },
  });

  return (
    <div className="flex h-full min-h-0 flex-col gap-2">
      {toolbar}
      {snoozeNotice && (
        <SnoozeQueueNotice
          mode={snoozeNotice}
          hiddenCount={queue.snoozedCount}
          onShow={() => queue.setShowSnoozed(true)}
          onHide={() => queue.setShowSnoozed(false)}
        />
      )}
      <TaskQueueBulkActionsBar bulk={queue.bulk} onSnooze={queue.openSnooze} />
      <div className="min-h-0 flex-1 overflow-auto">
        <DataTable
          table={table}
          isLoading={queue.isLoading}
          emptyMessage={queue.emptyMessage}
          renderRowLink={(task, className) => (
            <Link
              to="/tasks/$taskId"
              params={{ taskId: task.id }}
              search={detailSearch}
              className={className}
              aria-label={task.title}
            />
          )}
          onRowContextMenu={rowMenu.openAt}
        />
        {rowMenu.menu}
      </div>
      <DataTablePagination
        table={table}
        pageCount={queue.pageCount}
        pageIndex={queue.pageIndex}
        onPageChange={(newPageIndex) =>
          queue.updateSearchParams({ page: newPageIndex + 1 })
        }
      />
      {queue.snoozeTaskIds != null && (
        <SnoozeTaskDialog
          taskIds={queue.snoozeTaskIds}
          open
          onOpenChange={(open) => {
            if (!open) queue.closeSnooze();
          }}
        />
      )}
      {queue.deleteTaskId != null && (
        <DeleteTaskDialog
          taskId={queue.deleteTaskId}
          open
          onOpenChange={(open) => {
            if (!open) queue.closeDelete();
          }}
        />
      )}
    </div>
  );
}

function snoozeNoticeMode(
  queue: TaskQueueController,
): 'hidden' | 'included' | null {
  return snoozeQueueNotice({
    showsSnoozed: queue.showsSnoozed,
    explicitShow: queue.searchParams.showSnoozed === true,
    snoozedCount: queue.snoozedCount,
    anyVisibleSnoozed: queue.tasks.some((task) =>
      isActivelySnoozed(task.snoozedUntil),
    ),
  });
}
